import { useMemo, useState, type FormEvent } from 'react'
import {
  detectTrackingCarrier,
  normalizeTrackingNumber,
  resolveTrackingRequest,
  trackingCarriers,
  validateTrackingNumber,
  type TrackingCarrierId,
} from '../../data'

interface TrackingFormProps {
  idPrefix: string
  variant?: 'compact' | 'page'
}

export function TrackingForm({ idPrefix, variant = 'compact' }: TrackingFormProps) {
  const [input, setInput] = useState('')
  const [selectedCarrier, setSelectedCarrier] = useState<TrackingCarrierId | ''>('')
  const [error, setError] = useState<string | null>(null)

  const normalized = useMemo(() => normalizeTrackingNumber(input), [input])
  const validationError = normalized ? validateTrackingNumber(normalized) : null
  const detectedCarrier = validationError ? null : detectTrackingCarrier(normalized)
  const needsCarrierSelection = Boolean(normalized && !validationError && !detectedCarrier)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const result = resolveTrackingRequest(input, selectedCarrier || undefined)

    if (!result.ok) {
      setError(result.error)
      return
    }

    setError(null)
    window.location.assign(result.resolution.url)
  }

  return (
    <form
      className={`tracking-form tracking-form--${variant}`}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="tracking-form__row">
        <label className="sr-only" htmlFor={`${idPrefix}-number`}>Tracking number</label>
        <input
          id={`${idPrefix}-number`}
          name="trackingNumber"
          type="text"
          inputMode="text"
          autoComplete="off"
          maxLength={80}
          placeholder="ENTER A TRACKING NUMBER"
          value={input}
          aria-describedby={`${idPrefix}-message`}
          aria-invalid={Boolean(error)}
          onChange={(event) => {
            setInput(event.target.value)
            setSelectedCarrier('')
            setError(null)
          }}
        />
        <button type="submit">Track</button>
      </div>

      {needsCarrierSelection ? (
        <div className="tracking-form__carrier">
          <label htmlFor={`${idPrefix}-carrier`}>Carrier</label>
          <select
            id={`${idPrefix}-carrier`}
            value={selectedCarrier}
            onChange={(event) => {
              setSelectedCarrier(event.target.value as TrackingCarrierId | '')
              setError(null)
            }}
          >
            <option value="">Select carrier</option>
            {trackingCarriers.map((carrier) => (
              <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
            ))}
          </select>
        </div>
      ) : null}

      <p
        className={error ? 'tracking-form__message is-error' : 'tracking-form__message'}
        id={`${idPrefix}-message`}
        role={error ? 'alert' : undefined}
        aria-live="polite"
      >
        {error ?? (variant === 'page'
          ? 'Tracking details will open on the selected carrier’s official website.'
          : '')}
      </p>
    </form>
  )
}
